import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Enums, Tables } from './database.types';
import { useSupabase } from './provider';

export type Group = Tables<'groups'>;
export type GroupInvite = Tables<'group_invites'>;
export type GroupRole = Enums<'group_role'>;
export type MembershipStatus = Enums<'membership_status'>;

export interface MyGroup extends Group {
  myRole: GroupRole;
  myStatus: MembershipStatus;
}

export interface GroupMember {
  userId: string;
  role: GroupRole;
  status: MembershipStatus;
  joinedOn: string;
  displayName: string;
  avatarUrl: string | null;
}

export interface InvitePreview {
  groupId: string;
  groupName: string;
  message: string | null;
  memberCount: number;
  isValid: boolean;
  reason: string | null;
  /** Set when the invite also leads into a campaign of the group. */
  campaignId: string | null;
  campaignName: string | null;
  campaignSystem: string | null;
}

export interface RedeemResult {
  groupId: string;
  status: MembershipStatus;
  campaignId: string | null;
  campaignStatus: MembershipStatus | null;
}

export const queryKeys = {
  myGroups: (userId: string | undefined) => ['groups', 'mine', userId] as const,
  group: (id: string) => ['groups', id] as const,
  members: (id: string) => ['groups', id, 'members'] as const,
  invites: (id: string) => ['groups', id, 'invites'] as const,
  invitePreview: (code: string) => ['invite-preview', code.toUpperCase()] as const,
} as const;

/** Groups the signed-in user belongs to (any status), with their own role and status. */
export function useMyGroups(userId: string | undefined) {
  const supabase = useSupabase();
  return useQuery({
    queryKey: queryKeys.myGroups(userId),
    enabled: Boolean(userId),
    queryFn: async (): Promise<MyGroup[]> => {
      if (!userId) return [];
      const { data: memberships, error: mErr } = await supabase
        .from('group_members')
        .select('group_id, role, status')
        .eq('user_id', userId);
      if (mErr) throw mErr;
      if (!memberships.length) return [];
      const { data: groups, error: gErr } = await supabase
        .from('groups')
        .select('*')
        .in(
          'id',
          memberships.map((m) => m.group_id),
        )
        .order('name');
      if (gErr) throw gErr;
      const byId = new Map(memberships.map((m) => [m.group_id, m]));
      return groups.flatMap((g) => {
        const m = byId.get(g.id);
        return m ? [{ ...g, myRole: m.role, myStatus: m.status }] : [];
      });
    },
  });
}

export function useGroup(id: string) {
  const supabase = useSupabase();
  return useQuery({
    queryKey: queryKeys.group(id),
    queryFn: async (): Promise<Group | null> => {
      const { data, error } = await supabase.from('groups').select('*').eq('id', id).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useGroupMembers(id: string) {
  const supabase = useSupabase();
  return useQuery({
    queryKey: queryKeys.members(id),
    queryFn: async (): Promise<GroupMember[]> => {
      const { data: members, error } = await supabase
        .from('group_members')
        .select('user_id, role, status, joined_on')
        .eq('group_id', id);
      if (error) throw error;
      const { data: profiles, error: pErr } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url')
        .in(
          'id',
          members.map((m) => m.user_id),
        );
      if (pErr) throw pErr;
      const byId = new Map(profiles.map((p) => [p.id, p]));
      const rank: Record<GroupRole, number> = { owner: 0, admin: 1, member: 2 };
      return members
        .map((m) => ({
          userId: m.user_id,
          role: m.role,
          status: m.status,
          joinedOn: m.joined_on,
          displayName: byId.get(m.user_id)?.display_name ?? 'Player',
          avatarUrl: byId.get(m.user_id)?.avatar_url ?? null,
        }))
        .sort((a, b) => rank[a.role] - rank[b.role] || a.displayName.localeCompare(b.displayName));
    },
  });
}

/** Invites are only readable by admins and the owner; pass `enabled` accordingly. */
export function useGroupInvites(id: string, enabled: boolean) {
  const supabase = useSupabase();
  return useQuery({
    queryKey: queryKeys.invites(id),
    enabled,
    queryFn: async (): Promise<GroupInvite[]> => {
      const { data, error } = await supabase
        .from('group_invites')
        .select('*')
        .eq('group_id', id)
        .order('created_on', { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

/** What an invite code leads to. Works for signed-out visitors. */
export function useInvitePreview(code: string) {
  const supabase = useSupabase();
  return useQuery({
    queryKey: queryKeys.invitePreview(code),
    enabled: code.trim().length > 0,
    queryFn: async (): Promise<InvitePreview | null> => {
      const { data, error } = await supabase.rpc('redeem_preview', { p_code: code });
      if (error) throw error;
      const row = data[0];
      if (!row) return null;
      return {
        groupId: row.group_id,
        groupName: row.group_name,
        message: row.message,
        memberCount: Number(row.member_count),
        isValid: row.is_valid,
        reason: row.reason,
        campaignId: row.campaign_id,
        campaignName: row.campaign_name,
        campaignSystem: row.campaign_system,
      };
    },
  });
}

export function useCreateGroup() {
  const supabase = useSupabase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; description?: string }): Promise<string> => {
      const { data, error } = await supabase.rpc('create_group', {
        p_name: input.name,
        p_description: input.description ?? '',
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });
}

export function useRedeemInvite() {
  const supabase = useSupabase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string): Promise<RedeemResult> => {
      const { data, error } = await supabase.rpc('redeem_invite', { p_code: code });
      if (error) throw error;
      const row = data[0];
      if (!row) throw new Error('Invite could not be redeemed');
      return {
        groupId: row.group_id,
        status: row.status,
        campaignId: row.campaign_id,
        campaignStatus: row.campaign_status,
      };
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['groups'] }),
        qc.invalidateQueries({ queryKey: ['campaigns'] }),
      ]),
  });
}

export function useCreateInvite(groupId: string) {
  const supabase = useSupabase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { createdBy: string; message?: string }): Promise<GroupInvite> => {
      const { data, error } = await supabase
        .from('group_invites')
        .insert({ group_id: groupId, created_by: input.createdBy, message: input.message || null })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.invites(groupId) }),
  });
}

export function useRevokeInvite(groupId: string) {
  const supabase = useSupabase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string): Promise<void> => {
      const { error } = await supabase
        .from('group_invites')
        .update({ revoked_on: new Date().toISOString() })
        .eq('id', inviteId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.invites(groupId) }),
  });
}

// ---------------------------------------------------------------------------------------------------
// Member management and group settings (the rank rule is enforced by the RPCs, not here)
// ---------------------------------------------------------------------------------------------------
// A type alias (not an interface) so it is assignable to the generated `Json` column type.
export type GroupSettings = {
  joinViaCode: boolean;
  autoApproveMembers: boolean;
};

export function readGroupSettings(group: Pick<Group, 'settings'>): GroupSettings {
  const s = (group.settings ?? {}) as Partial<Record<keyof GroupSettings, unknown>>;
  return { joinViaCode: s.joinViaCode !== false, autoApproveMembers: s.autoApproveMembers !== false };
}

function useGroupMutation<TInput>(
  groupId: string,
  run: (supabase: ReturnType<typeof useSupabase>, input: TInput) => Promise<void>,
) {
  const supabase = useSupabase();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => run(supabase, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
    meta: { groupId },
  });
}

export function useSetMemberRole(groupId: string) {
  return useGroupMutation<{ userId: string; role: GroupRole }>(
    groupId,
    async (supabase, { userId, role }) => {
      const { error } = await supabase.rpc('set_member_role', {
        p_group_id: groupId,
        p_user_id: userId,
        p_role: role,
      });
      if (error) throw error;
    },
  );
}

export function useSetMemberStatus(groupId: string) {
  return useGroupMutation<{ userId: string; status: MembershipStatus }>(
    groupId,
    async (supabase, { userId, status }) => {
      const { error } = await supabase.rpc('set_member_status', {
        p_group_id: groupId,
        p_user_id: userId,
        p_status: status,
      });
      if (error) throw error;
    },
  );
}

export function useRemoveMember(groupId: string) {
  return useGroupMutation<string>(groupId, async (supabase, userId) => {
    const { error } = await supabase.rpc('remove_group_member', { p_group_id: groupId, p_user_id: userId });
    if (error) throw error;
  });
}

export function useTransferOwnership(groupId: string) {
  return useGroupMutation<string>(groupId, async (supabase, newOwnerId) => {
    const { error } = await supabase.rpc('transfer_group_ownership', {
      p_group_id: groupId,
      p_new_owner: newOwnerId,
    });
    if (error) throw error;
  });
}

export function useLeaveGroup(groupId: string) {
  return useGroupMutation<void>(groupId, async (supabase) => {
    const { error } = await supabase.rpc('leave_group', { p_group_id: groupId });
    if (error) throw error;
  });
}

export function useUpdateGroup(groupId: string) {
  return useGroupMutation<{ name?: string; description?: string; settings?: GroupSettings }>(
    groupId,
    async (supabase, input) => {
      const { error } = await supabase.from('groups').update(input).eq('id', groupId);
      if (error) throw error;
    },
  );
}
